import { createActionGroup, emptyProps, props } from '@ngrx/store';

import { ActionSource } from 'src/app/state/action-sources';
import {
    CompleteBlueSagePreauthMutation,
    GetBlueSageTerminalPushQuery,
    GetBlueSageTerminalPushStatusQuery,
    PushBlueSageToTerminalMutation,
    UpdateTransactionMutation,
} from 'src/generated/graphql.generated';

import { BlueSagePushKind, BlueSagePushPhase } from './bluesage-terminal-push.state';

export const BlueSageTerminalPushActions = createActionGroup({
    source: ActionSource.BLUESAGE_TERMINAL_PUSH,
    events: {
        // Two separately labelled buttons, never a "sale vs preauth" mode toggle.
        // A toggle puts an accounting decision in the hands of whoever is holding
        // the tablet and the failure is silent — money captured early, or a hold
        // left to expire unclaimed.
        'Send To Terminal Button Clicked': props<{
            transactionId: string;
            epi: string;
            amountMinor: number;
        }>(),
        'Authorize Button Clicked': props<{
            transactionId: string;
            epi: string;
            amountMinor: number;
        }>(),

        // Requirement: Complete is a TWO-STEP, never one click. This is step two —
        // the operator has already been told the customer has about 30 seconds at
        // the terminal and has confirmed they are standing there.
        'Complete Preauth Confirmed': props<{
            transactionId: string;
            epi: string;
            amountMinor: number;
        }>(),

        // Not a click. This is what a RETRY, and the payment dialog's create-then-
        // push sequence, dispatch — because replaying a `…ButtonClicked` action
        // that no button produced makes the devtools log read as clicks that never
        // happened, and actions are named after the events that really occurred.
        'Push Requested': props<{
            transactionId: string;
            epi: string;
            amountMinor: number;
            kind: BlueSagePushKind;
        }>(),

        // Not a click either. The payment dialog dispatches this the moment Pay or
        // Authorize is pressed, so the panel is on screen while the TRANSACTION is
        // still being written — before anything has been asked of BlueSage.
        'Push Preparing': props<{
            epi: string;
            amountMinor: number;
            kind: BlueSagePushKind;
        }>(),

        'Push Success': props<{
            result: PushBlueSageToTerminalMutation['pushBlueSageToTerminal'];
            kind: BlueSagePushKind;
        }>(),
        'Complete Success': props<{
            result: CompleteBlueSagePreauthMutation['completeBlueSagePreauth'];
        }>(),
        'Push Error': props<{ error: Error }>(),
        // Its own error, not `pushError`: "could not send that to the terminal"
        // is the wrong sentence when what failed was completing a hold the
        // customer's card is already carrying.
        'Complete Error': props<{ error: Error }>(),

        /** The poller's tick while the customer still has the device. */
        'Push Status Polled': props<{
            push: GetBlueSageTerminalPushQuery['blueSageTerminalPush'];
        }>(),
        'Push Status Poll Error': props<{ error: Error }>(),

        // The poller's tick for a push followed through its TRANSACTION rather
        // than a push id. Since 2026-09-03 the push mutations return before the
        // device answers, so until the middleware names a push id this is the
        // only way to know where the push is.
        'Push Status Checked': props<{
            status: GetBlueSageTerminalPushStatusQuery['blueSageTerminalPushStatus'];
        }>(),
        'Push Status Check Error': props<{ error: Error }>(),

        // The middleware refused the push AFTER the mutation had already
        // returned — an offline device, an amount over the cap, a completion
        // already running. The same refusal used to arrive on the mutation as
        // `pushError`; it now arrives through the status read, and it means
        // exactly what it meant then: nothing reached a card.
        'Push Refused After Start': props<{ message: string; kind: BlueSagePushKind }>(),

        // The push is not where the panel can see it any more — the device did
        // not answer in time, or the CRM lost track of it. The one place the
        // money will show up if the customer completed on the device.
        'Open Unmatched Payments Clicked': emptyProps(),

        // The job loaded with rows still marked as pushes in flight, each was read
        // once, and this many settled — the job re-reads when any did.
        'In Flight Pushes Settled': props<{ jobId: string; settled: number }>(),
        'In Flight Pushes Settle Error': props<{ error: Error }>(),

        /** Drives the elapsed counter and the tip countdown. */
        'Elapsed Ticked': emptyProps(),

        'Cancel Button Clicked': emptyProps(),
        'Cancel Success': props<{ status: string | null }>(),
        'Cancel Error': props<{ error: Error }>(),

        // Safe by construction: the idempotency key is derived from transaction id
        // + push type, so a retry REPLAYS the original push rather than queueing a
        // second prompt on a live reader. It reuses the same transaction and never
        // creates a second one.
        'Try Again Button Clicked': emptyProps(),

        // The push was refused before it reached a device, so the transaction
        // created to push against was cancelled rather than left on the job as a
        // pending payment that never happened.
        'Failed Transaction Discarded': props<{ transactionId: string }>(),

        // The device settled the push WITHOUT taking anything — the customer or the
        // crew cancelled it, or the card was declined. The transaction is closed as
        // cancelled rather than left `pending`, which is indistinguishable on the
        // job from a payment still in flight.
        'Unsettled Transaction Cancelled': props<{ data: UpdateTransactionMutation }>(),
        'Unsettled Transaction Cancel Error': props<{ error: Error }>(),

        'Done Button Clicked': props<{ phase: BlueSagePushPhase }>(),

        /** The panel is being taken down; nothing is being watched any more. */
        'Panel Dismissed': emptyProps(),
    },
});
